import cookies from 'js-cookie';
import ReactGA from 'react-ga4';
import { AnalyticsBrowser } from '@segment/analytics-next';
import { isImpersonationSession } from '../../helpers/auth/cognito';
import config from '../../../app-config';
import { captureError } from '../monitoring';

enum SegmentConsentState {
  Indeterminate,
  Allow,
  Deny,
}

let segmentClient = new AnalyticsBrowser();
let segmentConsentState = SegmentConsentState.Indeterminate;

export const getSegmentAnonymousId = (): string | undefined =>
  segmentClient.instance?.user().anonymousId() ??
  cookies.get('ajs_anonymous_id');

const getSegmentConsentState = (): SegmentConsentState => {
  if (window.cookiehub?.hasAnswered()) {
    return window.cookiehub?.hasConsented('analytics')
      ? SegmentConsentState.Allow
      : SegmentConsentState.Deny;
  }

  return SegmentConsentState.Indeterminate;
};

const initSegmentClient = () => {
  segmentConsentState = getSegmentConsentState();

  switch (segmentConsentState) {
    case SegmentConsentState.Allow:
      return segmentClient.load({ writeKey: config.segmentKey });
    case SegmentConsentState.Deny:
      return segmentClient.load(
        { writeKey: config.segmentKey },
        { disableClientPersistence: true }
      );
    default:
  }
};

export const updateSegmentConsentState = () => {
  const newConsentState = getSegmentConsentState();

  if (newConsentState === segmentConsentState) {
    // No need to handle consent state update
    return;
  }

  segmentConsentState = newConsentState;

  const isSegmentInitialised = segmentClient.instance?.initialized ?? false;

  if (isSegmentInitialised) {
    // Reset client to load new consent state at runtime
    segmentClient = new AnalyticsBrowser();
  }

  initSegmentClient().catch((e) =>
    captureError(new Error('Failed to init segment '), e)
  );
};

export const trackEventClientSide = (
  ...args: Parameters<typeof trackEventAsync>
) => {
  void trackEventAsync(...args);
};

// Do not use this directly, use the voided fire and forget trackEventClientSide instead
const trackEventAsync = async (
  event: string,
  properties: Record<string, any>
) => {
  const extendedProperties = {
    ...properties,
    'User Agent': navigator.userAgent,
    isImpersonationSession: isImpersonationSession(),
  };

  try {
    await segmentClient?.track(event, extendedProperties);
  } catch (e) {
    captureError(new Error('Failed to track event'), {
      error: e,
      event,
      extendedProperties,
    });
  }
};

export const trackAlias = (userId: string): void => {
  segmentClient
    ?.alias(userId)
    .catch((e) => captureError(new Error('Failed to alias'), e));
};

export const trackIdentity = (id: string, traits: Record<string, any>): void => {
  const extendedTraits = {
    ...traits,
    'User Agent': navigator.userAgent,
    isImpersonationSession: isImpersonationSession(),
  };

  segmentClient
    ?.identify(id, extendedTraits)
    .catch((e) => captureError(new Error('Failed to identify user'), e));
};

export const trackPageViewedClientSide = (
  ...args: Parameters<typeof trackPageViewedAsync>
) => {
  void trackPageViewedAsync(...args);
};

// Do not use this directly, use the voided fire and forget trackPageViewedClientSide instead
const trackPageViewedAsync = async (
  context?: Record<string, any>
): Promise<void> => {
  try {
    await segmentClient?.page();
  } catch (e) {
    captureError(new Error('Failed to track page view'), {
      error: e,
      ...context,
    });
  }
};

export const resetAnalytics = (): void => {
  segmentClient
    ?.reset()
    .catch((e) => captureError(new Error('Failed to reset analytics'), e));

  cookies.remove('_cioanonid');
  cookies.remove('_cioid');
  cookies.remove('ajs_anonymous_id');
  cookies.remove('ajs_user_id');

  localStorage.removeItem('ajs_user_traits');
  localStorage.removeItem('ajs_anonymous_id');
  localStorage.removeItem('ajs_user_id');
};

export const initGA = (): void => {
  if (!window.GA_INITIALIZED && process.env.googleAnalyticsId) {
    ReactGA?.initialize(process.env.googleAnalyticsId, {});

    window.GA_INITIALIZED = true;
  }
};

export const setCookie = (cookieName: string, cookieValue: string): void => {
  const existingCookie = cookies.get(cookieName);

  if (!existingCookie || existingCookie !== cookieValue) {
    cookies.set(cookieName, cookieValue, {
      ...(process.env.cookieSecure && {
        secure: Boolean(process.env.cookieSecure),
      }),
      expires: 21,
    });
  }
};
